import React, { useState, useEffect, useCallback } from 'react';
import { 
  PhoneIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import CreateCallModal from './CreateCallModal';
import LiveCallModal from './LiveCallModal';
import { useCallManagement } from '../contexts/CallManagementContext';
import { callsAPI } from '../services/callApi';

interface Call {
  id: number;
  leadId: number;
  phoneNumber: string;
  callType: 'live' | 'recorded' | 'scheduled';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'rescheduled';
  scheduledDate?: string;
  actualStartTime?: string;
  actualEndTime?: string;
  durationSeconds?: number;
  durationFormatted?: string;
  motiveAgenda: string;
  summary?: string;
  rescheduledFromCallId?: number;
  rescheduledToDate?: string;
  rescheduleReason?: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  leadFirstName?: string;
  leadLastName?: string;
  leadCompany?: string;
  leadEmail?: string;
  leadPhone?: string;
  leadMobileNumber?: string;
  createdByFirstName?: string;
  createdByLastName?: string;
}

interface LeadCallsTabProps {
  leadId: string;
  leadName: string;
  leadEmail?: string;
  leadPhone?: string;
  leadMobileNumber?: string;
}

const LeadCallsTab: React.FC<LeadCallsTabProps> = ({ leadId, leadName, leadEmail, leadPhone, leadMobileNumber }) => {
  const { 
    liveCall, 
    showLiveCallModal, 
    startCall, 
    endCall, 
    cancelCall, 
    rescheduleCall,
    setRefreshCalls 
  } = useCallManagement();
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCalls, setExpandedCalls] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showCreateCallModal, setShowCreateCallModal] = useState(false);

  const fetchCalls = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      const response = await import('../services/api').then(module => 
        module.leadsAPI.getLeadCalls(leadId)
      );
      
      if (append) {
        setCalls(prev => [...prev, ...(response.data || [])]);
      } else {
        setCalls(response.data || []);
      }
      
      // For now, we'll show all calls since the API doesn't have pagination
      // In a real implementation, you'd check response.pagination
      setHasMore(false);
    } catch (err: any) {
      console.error('Error fetching calls:', err);
      setError('Failed to fetch calls');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // Register refresh function with context
  useEffect(() => {
    setRefreshCalls(() => fetchCalls);
  }, [fetchCalls, setRefreshCalls]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setCurrentPage(prev => prev + 1);
      fetchCalls(currentPage + 1, true);
    }
  };

  const handleCallCreated = async (callData: any) => {
    try {
      const createdCall = await callsAPI.createCall(callData);
      
      // If this is a live call, start it immediately
      if (callData.callType === 'live') {
        const startedCall = await callsAPI.startCall(createdCall.id);
        startCall(startedCall);
      }
      
      // Refresh the calls list
      await fetchCalls();
      setShowCreateCallModal(false);
    } catch (error) {
      console.error('Error creating call:', error);
      setError('Failed to create call');
    }
  };

  // Handle call actions
  const handleStartCall = async (call: Call) => {
    try {
      const updatedCall = await callsAPI.startCall(call.id);
      startCall(updatedCall);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error starting call:', error);
      setError('Failed to start call');
    }
  };

  const handleEndCallWrapper = async (callId: number, summary: string, callPickedUp: boolean) => {
    try {
      await endCall(callId, summary, callPickedUp);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error ending call:', error);
      setError('Failed to end call');
    }
  };

  const handleCancelCallWrapper = async (callId: number, reason: string) => {
    try {
      await cancelCall(callId, reason);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error cancelling call:', error);
      setError('Failed to cancel call');
    }
  };

  const handleRescheduleCallWrapper = async (callId: number, newDate: string, reason: string) => {
    try {
      // Check if this is a live call that wasn't picked up
      if (liveCall && liveCall.id === callId) {
        await callsAPI.handleNotPickedUp(callId, { reason, rescheduleDate: newDate });
        await rescheduleCall(callId, newDate, reason);
      } else {
        await callsAPI.rescheduleCall(callId, { newDate, reason });
      }
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error rescheduling call:', error);
      setError('Failed to reschedule call');
    }
  };

  const toggleExpanded = (callId: number) => {
    setExpandedCalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'scheduled':
        return <ClockIcon className="w-4 h-4" />;
      case 'in_progress':
        return <PhoneIcon className="w-4 h-4" />;
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4" />;
      case 'rescheduled':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getCallTypeColor = (callType: string) => {
    switch (callType) {
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'recorded':
        return 'bg-blue-100 text-blue-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error}</p>
        <button 
          onClick={() => fetchCalls()}
          className="mt-4 text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <>
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <PhoneIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Calls Found</h3>
          <p className="text-gray-600 mb-6">No calls have been made to {leadName} yet.</p>
          <button
            onClick={() => setShowCreateCallModal(true)}
            disabled={!!liveCall}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title={liveCall ? 'End current call before starting a new one' : 'Create a new call'}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Call
          </button>
        </div>

        {/* Create Call Modal */}
        {showCreateCallModal && leadName && (
          <CreateCallModal
            leads={[{
              id: parseInt(leadId),
              firstName: leadName.split(' ')[0] || '',
              lastName: leadName.split(' ').slice(1).join(' ') || '',
              email: leadEmail || '',
              company: '',
              phoneNumbers: [
                ...(leadPhone ? [{ number: leadPhone, type: 'phone' }] : []),
                ...(leadMobileNumber ? [{ number: leadMobileNumber, type: 'mobile' }] : [])
              ]
            }]}
            preSelectedLead={{
              id: parseInt(leadId),
              firstName: leadName.split(' ')[0] || '',
              lastName: leadName.split(' ').slice(1).join(' ') || '',
              email: leadEmail || '',
              company: '',
              phoneNumbers: [
                ...(leadPhone ? [{ number: leadPhone, type: 'phone' }] : []),
                ...(leadMobileNumber ? [{ number: leadMobileNumber, type: 'mobile' }] : [])
              ]
            }}
            lockLeadSelection={true}
            onClose={() => setShowCreateCallModal(false)}
            onCallCreated={handleCallCreated}
          />
        )}
      </>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with Create Call Button */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Calls</h3>
        <button
          onClick={() => setShowCreateCallModal(true)}
          disabled={!!liveCall}
          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          title={liveCall ? 'End current call before starting a new one' : 'Create a new call'}
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Call
        </button>
      </div>

      {calls.map((call) => {
        const isExpanded = expandedCalls.has(call.id);
        return (
          <div key={call.id} className="bg-white rounded-lg shadow-sm border">
            {/* Compact Header */}
            <div 
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleExpanded(call.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <PhoneIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        {call.callType.charAt(0).toUpperCase() + call.callType.slice(1)} Call
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(call.status)}`}>
                        {getStatusIcon(call.status)}
                        <span className="ml-1">{call.status.charAt(0).toUpperCase() + call.status.slice(1)}</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="flex items-center">
                        <PhoneIcon className="w-3 h-3 mr-1" />
                        {call.phoneNumber}
                      </span>
                      {call.scheduledDate && (
                        <span className="flex items-center">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {formatDate(call.scheduledDate)}
                        </span>
                      )}
                      {call.durationFormatted && (
                        <span className="flex items-center">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {call.durationFormatted}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right text-xs text-gray-500">
                    <div>{formatDate(call.createdAt)}</div>
                    <div>by {call.createdByFirstName} {call.createdByLastName}</div>
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Motive/Agenda</h5>
                    <p className="text-sm text-gray-700 bg-white p-3 rounded-lg">
                      {call.motiveAgenda}
                    </p>
                  </div>

                  {call.summary && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 mb-2">Summary</h5>
                      <p className="text-sm text-gray-700 bg-white p-3 rounded-lg whitespace-pre-wrap">
                        {call.summary}
                      </p>
                    </div>
                  )}

                  {call.rescheduledToDate && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <h5 className="text-sm font-medium text-orange-900 mb-2">Reschedule Information</h5>
                      <div className="space-y-1 text-sm text-orange-700">
                        <p>Rescheduled to: {formatDate(call.rescheduledToDate)}</p>
                        {call.rescheduleReason && (
                          <p>Reason: {call.rescheduleReason}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {call.actualStartTime && call.actualEndTime && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h5 className="text-sm font-medium text-green-900 mb-2">Call Details</h5>
                      <div className="space-y-1 text-sm text-green-700">
                        <p>Started: {formatDate(call.actualStartTime)}</p>
                        <p>Ended: {formatDate(call.actualEndTime)}</p>
                        {call.durationFormatted && (
                          <p>Duration: {call.durationFormatted}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    {call.status === 'scheduled' && call.callType === 'live' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartCall(call);
                        }}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <PhoneIcon className="w-4 h-4 mr-2" />
                        Start Call
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center py-4">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading...
              </>
            ) : (
              'Load More Calls'
            )}
          </button>
        </div>
      )}

      {/* Create Call Modal */}
      {showCreateCallModal && leadName && (
        <CreateCallModal
            leads={[{
              id: parseInt(leadId),
              firstName: leadName.split(' ')[0] || '',
              lastName: leadName.split(' ').slice(1).join(' ') || '',
              email: leadEmail || '',
              company: '',
              phoneNumbers: [
                ...(leadPhone ? [{ number: leadPhone, type: 'phone' }] : []),
                ...(leadMobileNumber ? [{ number: leadMobileNumber, type: 'mobile' }] : [])
              ]
            }]}
            preSelectedLead={{
              id: parseInt(leadId),
              firstName: leadName.split(' ')[0] || '',
              lastName: leadName.split(' ').slice(1).join(' ') || '',
              email: leadEmail || '',
              company: '',
              phoneNumbers: [
                ...(leadPhone ? [{ number: leadPhone, type: 'phone' }] : []),
                ...(leadMobileNumber ? [{ number: leadMobileNumber, type: 'mobile' }] : [])
              ]
            }}
            lockLeadSelection={true}
            onClose={() => setShowCreateCallModal(false)}
            onCallCreated={handleCallCreated}
          />
      )}

      {/* Live Call Modal */}
      {liveCall && (
        <LiveCallModal
          call={liveCall}
          onEndCall={handleEndCallWrapper}
          onReschedule={handleRescheduleCallWrapper}
          onCancelCall={handleCancelCallWrapper}
        />
      )}
    </div>
  );
};

export default LeadCallsTab;
